import React, { useState, useEffect } from 'react';
import { Table, Typography, Card, Space, Tag, Spin, Alert, Empty, Statistic, Row, Col } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, TrophyOutlined, UserOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;

const RobotRepairReport = () => {
  const [examRecords, setExamRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState({
    totalStudents: 0,
    avgScore: 0,
    avgTime: 0,
    passRate: 0
  });

  useEffect(() => {
    const fetchRobotRepairExams = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('正在获取工业机器人维修考试成绩，无需认证');
        
        const res = await axios.get('/api/exams/mr-robot-repair/public');
        
        console.log('获取到工业机器人维修考试成绩:', res.data);
        
        // 计算统计数据
        if (res.data.length > 0) {
          const totalScore = res.data.reduce((sum, exam) => sum + exam.score, 0);
          const avgScore = Math.round(totalScore / res.data.length);
          
          const totalTime = res.data.reduce((sum, exam) => sum + exam.completionTime, 0);
          const avgTime = Math.round((totalTime / res.data.length) * 10) / 10;
          
          const passedCount = res.data.filter(exam => exam.passed).length;
          const passRate = Math.round((passedCount / res.data.length) * 100);
          
          // 获取唯一学生数量
          const uniqueStudents = new Set(res.data.map(exam => exam.username));
          
          setStatistics({
            totalStudents: uniqueStudents.size,
            avgScore,
            avgTime,
            passRate
          });
        }
        
        // 给每条记录添加一个 key 属性，用于 Table 组件
        const recordsWithKeys = res.data.map((record, index) => ({
          ...record,
          key: record._id || index
        }));
        
        setExamRecords(recordsWithKeys);
        setLoading(false);
      } catch (err) {
        console.error('获取工业机器人维修考试成绩失败:', err);
        setError(`获取考试成绩失败: ${err.response?.status} ${err.response?.data?.msg || err.message}`);
        setLoading(false);
      }
    };

    fetchRobotRepairExams();
  }, []);

  const columns = [
    {
      title: '学生姓名',
      dataIndex: 'studentName',
      key: 'studentName',
    },
    {
      title: '分数',
      dataIndex: 'score',
      key: 'score',
      sorter: (a, b) => a.score - b.score,
      render: score => (
        <span style={{ color: score >= 70 ? '#52c41a' : '#f5222d', fontWeight: 'bold' }}>
          {score}
        </span>
      ),
    },
    {
      title: '完成时间',
      dataIndex: 'completionTime',
      key: 'completionTime',
      sorter: (a, b) => a.completionTime - b.completionTime,
      render: time => `${(time * 60).toFixed(1)} 秒`,
    },
    {
      title: '结果',
      dataIndex: 'passed',
      key: 'passed',
      render: passed => (
        <Tag color={passed ? 'success' : 'error'} icon={passed ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {passed ? '通过' : '未通过'}
        </Tag>
      ),
      filters: [
        { text: '通过', value: true },
        { text: '未通过', value: false },
      ],
      onFilter: (value, record) => record.passed === value,
    },
    {
      title: '考试时间',
      dataIndex: 'date',
      key: 'date',
      render: date => new Date(date).toLocaleString(),
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
      defaultSortOrder: 'descend',
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p>加载考试成绩...</p>
      </div>
    );
  }

  if (error) {
    return <Alert message="错误" description={error} type="error" showIcon />;
  }

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Title level={2}>MR工业机器人维修考试成绩</Title>
        
        <Row gutter={16}>
          <Col span={6}>
            <Card>
              <Statistic 
                title="参与学生数" 
                value={statistics.totalStudents} 
                prefix={<UserOutlined />} 
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic 
                title="平均分数" 
                value={statistics.avgScore} 
                prefix={<TrophyOutlined />} 
                valueStyle={{ color: statistics.avgScore >= 70 ? '#3f8600' : '#cf1322' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic 
                title="平均完成时间" 
                value={(statistics.avgTime * 60).toFixed(1)}
                suffix="秒" 
                prefix={<ClockCircleOutlined />} 
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic 
                title="通过率" 
                value={statistics.passRate} 
                suffix="%" 
                valueStyle={{ color: statistics.passRate >= 60 ? '#3f8600' : '#cf1322' }}
              />
            </Card>
          </Col>
        </Row>
        
        {examRecords.length > 0 ? (
          <Table 
            columns={columns} 
            dataSource={examRecords} 
            pagination={{ pageSize: 10 }}
            rowKey="key"
          />
        ) : (
          <Empty description="暂无工业机器人维修考试记录" />
        )}
      </Space>
    </Card>
  );
};

export default RobotRepairReport; 